import { useEffect, useRef } from 'react';

export default function TopoAnimation() {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Light theme — soft slate grid
    const GOLD = 'rgba(148,163,184,';

    // Grid config
    const MAJOR_SPACING = 120;   // px between major section lines
    const MINOR_DIVS    = 4;     // subdivisions per section
    const MINOR_SPACING = MAJOR_SPACING / MINOR_DIVS;
    const DRIFT_X       = 0.012; // px per ms — slow horizontal drift
    const DRIFT_Y       = 0.008; // px per ms — slow vertical drift

    let start = null;

    const loop = (ts) => {
      if (!start) start = ts;
      const elapsed = ts - start;

      const W = canvas.width;
      const H = canvas.height;

      ctx.clearRect(0, 0, W, H);

      // How far has the grid drifted?
      const ox = (elapsed * DRIFT_X) % MAJOR_SPACING;
      const oy = (elapsed * DRIFT_Y) % MAJOR_SPACING;

      // ── Minor grid lines ──────────────────────────────────────────────────
      ctx.lineWidth   = 0.4;
      ctx.strokeStyle = GOLD + '0.10)';
      ctx.beginPath();

      // Vertical minor
      for (let x = -ox; x < W + MAJOR_SPACING; x += MINOR_SPACING) {
        // Skip if this is a major line position
        const isMajor = Math.abs(x % MAJOR_SPACING) < 1 || Math.abs(x % MAJOR_SPACING - MAJOR_SPACING) < 1;
        if (isMajor) continue;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
      }
      // Horizontal minor
      for (let y = -oy; y < H + MAJOR_SPACING; y += MINOR_SPACING) {
        const isMajor = Math.abs(y % MAJOR_SPACING) < 1 || Math.abs(y % MAJOR_SPACING - MAJOR_SPACING) < 1;
        if (isMajor) continue;
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
      }
      ctx.stroke();

      // ── Major section lines ───────────────────────────────────────────────
      ctx.lineWidth   = 0.9;
      ctx.strokeStyle = GOLD + '0.22)';
      ctx.beginPath();

      for (let x = -ox; x < W + MAJOR_SPACING; x += MAJOR_SPACING) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
      }
      for (let y = -oy; y < H + MAJOR_SPACING; y += MAJOR_SPACING) {
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
      }
      ctx.stroke();

      // ── Corner tick marks at major intersections ──────────────────────────
      const TICK = 6;
      ctx.lineWidth   = 1.2;
      ctx.strokeStyle = GOLD + '0.35)';

      for (let x = -ox; x < W + MAJOR_SPACING; x += MAJOR_SPACING) {
        for (let y = -oy; y < H + MAJOR_SPACING; y += MAJOR_SPACING) {
          // Small cross at each section corner
          ctx.beginPath();
          ctx.moveTo(x - TICK, y); ctx.lineTo(x + TICK, y);
          ctx.moveTo(x, y - TICK); ctx.lineTo(x, y + TICK);
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        pointerEvents: 'none',
      }}
    />
  );
}
